import { XIcon } from "@phosphor-icons/react/dist/ssr/X";
import { clsx } from "clsx";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";

import { analyticsEvent } from "@up/analytics";
import {
  FILTER_NAMES,
  filterCohorts,
  generateDurationFilter,
  generateStartDateFilter,
  handleCohortFilterChange,
  logError,
  resetCohortFilters,
  useTheme,
} from "@up/data";
import { Filters, Ticker } from "@up/ui";

import { useCart } from "../CartContext";

import type { CartContextType } from "../CartContext";
import type { FilterType, TCatalogueItem, TInventoryItem } from "@up/data";
import type { ChangeEvent, ReactNode } from "react";

const GRID_CLASSES = "grid grid-cols-[1rem_1fr_1rem]";

const contentAnimationProps = {
  initial: { opacity: 0, transform: "translateY(10px)", height: 0 },
  animate: {
    opacity: 1,
    transform: "translateY(0)",
    height: "auto",
    transition: {
      duration: 0.2,
      ease: [0.17, 0.55, 0.55, 1],
    },
  },
  exit: { opacity: 0, transform: "translateY(10px)", height: 0 },
} as const;

const itemHasCohorts = function (item: TCatalogueItem) {
  return Boolean(item.cohorts.length);
};

const itemNotInCart = function (cart: CartContextType, item: TCatalogueItem) {
  if (!cart?.data?.lineItems?.length) {
    return true;
  }
  const isInCart = cart.data?.lineItems
    .map((i) => {
      return i.catalogueItem._id;
    })
    .includes(item._id);
  return !isInCart;
};

const generateFilters = function (
  inventoryItems: TInventoryItem[],
  previousFilters?: FilterType[],
) {
  const enabledFilters = {
    startDate: true,
    duration: true,
    course: false,
  };

  const previousF = previousFilters ?? [];

  const startDateFilter = previousF.find(
    (f) => f.name === FILTER_NAMES.START_DATE,
  );

  const durationFilter = previousF.find(
    (f) => f.name === FILTER_NAMES.DURATION,
  );

  return [
    generateStartDateFilter({
      enabledFilters,
      inventoryItems,
      previousFilter: startDateFilter,
    }),
    generateDurationFilter({
      enabledFilters,
      inventoryItems,
      previousFilter: durationFilter,
    }),
  ];
};

const _formatDateForSummary = function (date: string) {
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeZone: "America/Los_Angeles",
  }).format(new Date(date));
};

export const Upsell = function Upsell({
  analytics,
  openDefault = true,
  showToggle = true,
  title = "Interested in another course?",
}: {
  analytics: {
    [key: string]: any;
    location: string;
  };
  openDefault?: boolean;
  showToggle?: boolean;
  title?: ReactNode;
}) {
  const theme = useTheme();
  const cart = useCart();

  const [show, setShow] = useState(openDefault);
  const [selectedCatalogueItem, setSelectedCatalogueItem] = useState<
    TCatalogueItem | undefined
  >(undefined);
  const [selectedInventoryItem, setSelectedInventoryItem] = useState<
    TInventoryItem | undefined
  >(undefined);
  const [catalogueItems, setCatalogueItems] = useState<TCatalogueItem[]>([]);

  const [filters, setFilters] = useState(() => {
    return generateFilters([]);
  });

  useEffect(() => {
    const go = async function () {
      const _catalogueItems = cart.inventory
        .filter(itemHasCohorts)
        .filter((item) => itemNotInCart(cart, item));
      setCatalogueItems(
        _catalogueItems.sort((a: TCatalogueItem, b: TCatalogueItem) =>
          a.name.localeCompare(b.name),
        ),
      );

      const defaultCI = _catalogueItems?.length
        ? _catalogueItems[0]
        : undefined;
      setSelectedCatalogueItem(defaultCI);
      setSelectedInventoryItem(defaultCI?.cohorts[0]);

      if (defaultCI) {
        setFilters(generateFilters(defaultCI.cohorts, filters));
      }
    };

    go();
  }, [cart.data?.lineItems, cart.inventory]);

  const filteredCohorts = filterCohorts(
    selectedCatalogueItem?.cohorts ?? [],
    filters,
  );

  useEffect(() => {
    if (!selectedCatalogueItem) {
      return;
    }
    if (!selectedInventoryItem) {
      return;
    }

    /*
      When a filter changes, the currently selected inventory item
      may or may not be in the new list. If it is, we don't want to clear it
    */
    const filteredCohortsContainsSelectedInventoryItem = filteredCohorts.find(
      (c) => {
        return c._id === selectedInventoryItem._id;
      },
    );
    if (!filteredCohortsContainsSelectedInventoryItem) {
      setSelectedInventoryItem(filteredCohorts[0]);
    }
  }, [filters]);

  const handleFilterToggle = function (filterName: string, optionName: string) {
    setFilters(handleCohortFilterChange(filters, filterName, optionName));
  };

  const handleCatalogueItemChange = function (
    e: ChangeEvent<HTMLSelectElement>,
  ) {
    const ci = catalogueItems.find((item) => {
      return item._id === e.target.value;
    });
    if (ci) {
      setSelectedCatalogueItem(ci);
      setFilters(generateFilters(ci.cohorts, filters));
      setSelectedInventoryItem(ci.cohorts[0]);
    }
  };

  const addToCart = async (id: string) => {
    analyticsEvent({
      event: "addToCart",
      ...analytics,
    });
    try {
      await cart.addItem(id);
      setShow(false);
    } catch (err) {
      logError({ action: "UPSELL_ADD_TO_CART", error: err });
    }
  };

  if (!cart || !catalogueItems?.length) {
    return null;
  }

  return (
    <div className="@container">
      <div
        className={clsx(
          "relative overflow-hidden border border-solid border-yellow-300 rounded",
          show ? "bg-yellow-50" : "animation-shine bg-yellow-100",
        )}
      >
        <button
          className={`${GRID_CLASSES} w-full pt-4 outline-none`}
          onClick={() => {
            setShow(!show);
          }}
          style={{ boxShadow: "none" }}
        >
          <div className="col-start-2 col-span-1 flex flex-col mb-4 font-bold justify-between text-left @md:flex-row">
            {title}
            {showToggle ? (
              <span className="gap-1 flex items-center text-sm font-normal opacity-70">
                <XIcon
                  size="16"
                  style={{
                    transform: show ? "rotate(0)" : "rotate(45deg)",
                    transition: "transform 0.2s",
                  }}
                />{" "}
                {show ? "no thanks" : "show me more"}
              </span>
            ) : null}
          </div>
        </button>
        <AnimatePresence>
          {show ? (
            <motion.div {...contentAnimationProps}>
              <div className={`${GRID_CLASSES} mb-2`}>
                <div className="col-start-2 col-span-1">
                  <label htmlFor="add-to-cart-in-cart-catalogue-item">
                    Choose a program
                  </label>
                  <select
                    className="w-full mb-2 bg-white border border-gray-400 rounded py-1 px-3 shadow-sm"
                    id="add-to-cart-in-cart-catalogue-item"
                    name="program"
                    value={selectedCatalogueItem?._id ?? "0"}
                    onChange={handleCatalogueItemChange}
                  >
                    {catalogueItems?.map((p) => {
                      return (
                        <option key={p._id} value={p._id}>
                          {p.name}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              <div className={GRID_CLASSES}>
                <div className="col-start-2 col-span-1">
                  <Filters
                    filters={filters}
                    handleFilterToggle={handleFilterToggle}
                  />
                </div>
              </div>

              {selectedCatalogueItem ? (
                <div className={GRID_CLASSES}>
                  <div className="col-start-2 col-span-1">
                    {filteredCohorts.length ? (
                      <>
                        <label htmlFor="add-to-cart-in-cart-cohort">
                          <Ticker
                            value={`Choose a date (${filteredCohorts.length.toString()} available)`}
                          />
                        </label>
                        <select
                          className="w-full bg-white border border-gray-400 rounded py-1 px-3 shadow-sm"
                          id="add-to-cart-in-cart-cohort"
                          name="cohort"
                          value={selectedInventoryItem?._id ?? "0"}
                          onChange={(e) => {
                            setSelectedInventoryItem(
                              selectedCatalogueItem.cohorts.find((c) => {
                                return c._id === e.target.value;
                              }),
                            );
                          }}
                        >
                          <option key="_" value="0" />

                          {filteredCohorts.map((c) => {
                            return (
                              <option key={c._id} value={c._id}>
                                {c.name}
                              </option>
                            );
                          })}
                        </select>
                      </>
                    ) : (
                      <div className="animation-fade-in">
                        <div className="pt-2 mb-2">
                          No courses match these filters
                        </div>
                        <button
                          className={`${theme.buttonPrimaryClass} w-full`}
                          onClick={() => {
                            setFilters(resetCohortFilters(filters));
                          }}
                        >
                          Reset Filters
                        </button>
                      </div>
                    )}
                    <div aria-hidden className="h-6" />
                  </div>
                </div>
              ) : null}
              <div
                className={`${GRID_CLASSES} border-t border-dashed border-yellow-300`}
              >
                <div className="col-start-2 col-span-1">
                  <div aria-hidden className="h-4" />
                  <AnimatePresence>
                    {selectedInventoryItem && filteredCohorts.length ? (
                      <motion.div
                        className="leading-tight"
                        {...contentAnimationProps}
                      >
                        <div className="opacity-70">
                          {_formatDateForSummary(
                            selectedInventoryItem.startDate,
                          )}{" "}
                          —{" "}
                          {_formatDateForSummary(selectedInventoryItem.endDate)}{" "}
                          ({selectedInventoryItem.cohortLength})
                        </div>
                        <div className="font-bold @lg:text-lg">
                          {selectedInventoryItem.catalogueItem.name}
                        </div>
                        <div aria-hidden className="h-2" />
                      </motion.div>
                    ) : null}
                  </AnimatePresence>

                  {selectedInventoryItem && filteredCohorts.length ? (
                    <div
                      className={clsx(
                        "flex items-center justify-between gap-4 pt-3",
                        "@md:justify-start",
                      )}
                    >
                      <button
                        className={theme.buttonPrimaryClass}
                        data-location={analytics.location}
                        data-testid="add-to-cart-button"
                        disabled={cart.isAddingItem}
                        onClick={() => {
                          addToCart(selectedInventoryItem._id);
                        }}
                      >
                        Add to Cart
                      </button>
                      <span className="animation-fade-in">
                        <Ticker
                          value={selectedInventoryItem.formattedRetailPrice}
                        />
                      </span>
                    </div>
                  ) : (
                    <button
                      disabled
                      className={`${theme.buttonPrimaryClass} opacity-50 cursor-not-allowed`}
                    >
                      Add to Cart
                    </button>
                  )}
                  <div aria-hidden className="h-4" />
                </div>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
};
